/**
 * The answer to "how close are we to being able to sign a BAA", plus the numbers that
 * explain it.
 */
import { AlertTriangle, HelpCircle, ShieldCheck, Timer } from 'lucide-react';
import type { HipaaActivityEntry, HipaaItem } from '../types/hipaaTypes';
import { STATUS_LABELS } from '../types/hipaaTypes';
import { completionTrend, type HipaaStats } from '../utils/hipaaStats';
import { PriorityBadge, StatusPill } from './Badges';
import { CompletionTrend, ProgressLegend, StackedProgressBar } from './Charts';

const Card = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
    <div className="mb-3">
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const StatTile = ({
  label,
  value,
  hint,
  tone = 'neutral',
  onClick,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'neutral' | 'warn' | 'danger' | 'good';
  onClick?: () => void;
}) => {
  const tones = {
    neutral: 'text-gray-900',
    warn: 'text-amber-600',
    danger: 'text-red-600',
    good: 'text-green-600',
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${
        onClick ? 'cursor-pointer hover:border-gray-300 transition-colors' : ''
      }`}
      onClick={onClick}
    >
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${tones[tone]}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  );
};

/**
 * The headline. P0 is defined in the checklist as "must be done before signing the first BAA
 * / receiving PHI as a business associate", so its completion is the readiness number.
 */
const ReadinessGauge = ({ stats }: { stats: HipaaStats }) => {
  const blockers = stats.p0Total - stats.p0Done;
  const tone =
    blockers === 0 ? 'text-green-600' : stats.baaReadiness >= 50 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            BAA readiness
          </p>
          <p className={`mt-1 text-5xl font-bold ${tone}`}>{stats.baaReadiness}%</p>
          <p className="mt-2 text-sm text-gray-600">
            {blockers === 0 ? (
              <span className="font-medium text-green-700">
                All P0 items closed — no remaining blockers on the first BAA.
              </span>
            ) : (
              <>
                <span className="font-semibold text-gray-900">{blockers}</span> P0 blocker
                {blockers === 1 ? '' : 's'} remaining before the first BAA can be signed.
              </>
            )}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400">P0 items</p>
          <p className="text-lg font-semibold text-gray-900">
            {stats.p0Done} / {stats.p0Total}
          </p>
        </div>
      </div>

      <div className="mt-4">
        <div className="w-full bg-gray-100 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all duration-500 ${
              blockers === 0 ? 'bg-green-500' : 'bg-red-500'
            }`}
            style={{ width: `${Math.max(stats.baaReadiness, 1)}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const ItemRow = ({ item, onOpen }: { item: HipaaItem; onOpen: (item: HipaaItem) => void }) => (
  <li>
    <button
      type="button"
      onClick={() => onOpen(item)}
      className="w-full text-left flex items-start gap-2 py-1.5 hover:bg-gray-50 rounded px-1 -mx-1 transition-colors"
    >
      <span className="text-[10px] font-mono text-gray-400 shrink-0 w-10 pt-0.5">
        §{item.section}
      </span>
      <span className="flex-1 text-xs text-gray-700">{item.title}</span>
      <span className="shrink-0">
        <PriorityBadge priorities={item.priorities} />
      </span>
    </button>
  </li>
);

const RELATIVE_UNITS: Array<[limit: number, divisor: number, unit: Intl.RelativeTimeFormatUnit]> = [
  [60, 1, 'second'],
  [3600, 60, 'minute'],
  [86400, 3600, 'hour'],
  [604800, 86400, 'day'],
  [2629800, 604800, 'week'],
];

const relativeTime = (iso: string | null): string => {
  if (!iso) return '';
  const seconds = (Date.now() - new Date(iso).getTime()) / 1000;
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

  for (const [limit, divisor, unit] of RELATIVE_UNITS) {
    if (seconds < limit) return formatter.format(-Math.floor(seconds / divisor), unit);
  }
  return formatter.format(-Math.floor(seconds / 2629800), 'month');
};

const describeActivity = (entry: HipaaActivityEntry): string => {
  switch (entry.type) {
    case 'status':
      return `${STATUS_LABELS[entry.from as keyof typeof STATUS_LABELS] ?? entry.from} → ${
        STATUS_LABELS[entry.to as keyof typeof STATUS_LABELS] ?? entry.to
      }`;
    case 'effort':
      return `effort ${entry.from ?? 'unset'} → ${entry.to ?? 'unset'}`;
    case 'blocked':
      return entry.to === 'true' ? `blocked: ${entry.text ?? 'no reason given'}` : 'unblocked';
    case 'comment':
      return entry.text ?? '';
  }
};

const ActivityFeed = ({
  activity,
  onOpen,
  items,
}: {
  activity: HipaaActivityEntry[];
  items: HipaaItem[];
  onOpen: (item: HipaaItem) => void;
}) => {
  if (activity.length === 0) {
    return <p className="text-xs text-gray-400 py-4 text-center">Nothing logged yet.</p>;
  }

  const byId = new Map(items.map((item) => [item.id, item]));

  return (
    <ul className="space-y-2.5">
      {activity.slice(0, 20).map((entry) => {
        const item = byId.get(entry.itemId);
        return (
          <li key={entry.id} className="text-xs">
            <button
              type="button"
              disabled={!item}
              onClick={() => item && onOpen(item)}
              className="text-left w-full disabled:cursor-default"
            >
              <span className="font-medium text-gray-800 hover:text-blue-600 transition-colors">
                {entry.itemTitle}
              </span>
              <span className="text-gray-500"> — {describeActivity(entry)}</span>
              <span className="block text-[10px] text-gray-400 mt-0.5">
                {entry.actor.email || 'unknown'} · {relativeTime(entry.at)}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

interface HipaaOverviewProps {
  items: HipaaItem[];
  activity: HipaaActivityEntry[];
  stats: HipaaStats;
  onOpenItem: (item: HipaaItem) => void;
  onGoToTab: (tab: string) => void;
}

export const HipaaOverview = ({
  items,
  activity,
  stats,
  onOpenItem,
  onGoToTab,
}: HipaaOverviewProps) => (
  <div className="space-y-4">
    <ReadinessGauge stats={stats} />

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <StatTile
        label="Overall progress"
        value={`${stats.weightedCompletion}%`}
        hint="Weighted by priority; partial counts half"
      />
      <StatTile
        label="Blocked"
        value={String(stats.blocked.length)}
        hint={stats.blocked.length ? 'Stalled, not merely unstarted' : 'Nothing stalled'}
        tone={stats.blocked.length ? 'danger' : 'good'}
      />
      <StatTile
        label="Open questions"
        value={`${stats.openVerification}/${stats.verificationTotal}`}
        hint="Need a console or vendor check"
        tone={stats.openVerification ? 'warn' : 'good'}
        onClick={() => onGoToTab('verification')}
      />
      <StatTile
        label="Vendors without BAA"
        value={`${stats.vendorsWithoutBaa}/${stats.vendorsTotal}`}
        hint="The largest P0 cluster"
        tone={stats.vendorsWithoutBaa ? 'danger' : 'good'}
        onClick={() => onGoToTab('vendors')}
      />
      <StatTile
        label="Required specs"
        value={`${stats.requiredDone}/${stats.requiredTotal}`}
        hint="Non-negotiable under §164.306(d)"
        tone={stats.requiredDone === stats.requiredTotal ? 'good' : 'warn'}
      />
      <StatTile
        label="Addressable specs"
        value={`${stats.addressableDone}/${stats.addressableTotal}`}
        hint="Implement, or document an alternative"
      />
      <StatTile
        label="Trust-list coverage"
        value={`${stats.trustListDone}/${stats.trustListTotal}`}
        hint="What a partner review scores"
        onClick={() => onGoToTab('trust-list')}
      />
      <StatTile
        label="Remaining effort"
        value={`≈ ${stats.remainingEffort} pts`}
        hint={stats.unsized > 0 ? `${stats.unsized} open items unsized` : 'All open items sized'}
        tone={stats.unsized > 0 ? 'warn' : 'neutral'}
      />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card title="Progress by section" subtitle="N/A items excluded from each bar">
        <div className="space-y-3">
          {stats.bySection.map((section) => (
            <div key={section.section}>
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <span className="text-xs text-gray-700 truncate">
                  <span className="font-mono text-gray-400">§{section.section}</span>{' '}
                  {section.title}
                </span>
                <span className="text-[10px] text-gray-400 shrink-0">
                  {section.breakdown.done}/{section.breakdown.total - section.breakdown.na}
                  {section.openP0 > 0 && (
                    <span className="ml-1.5 text-red-500 font-medium">{section.openP0} P0</span>
                  )}
                </span>
              </div>
              <StackedProgressBar breakdown={section.breakdown} />
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-gray-100">
          <ProgressLegend />
        </div>
      </Card>

      <div className="space-y-4">
        <Card title="By priority">
          <div className="space-y-3">
            {stats.byPriority.map((group) => (
              <div key={group.priority}>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700">{group.priority}</span>
                  <span className="text-[10px] text-gray-400">
                    {group.done}/{group.total}
                  </span>
                </div>
                <StackedProgressBar breakdown={group.breakdown} />
              </div>
            ))}
          </div>
        </Card>

        <Card title="Completion trend" subtitle="Items moved to Done, per week">
          <CompletionTrend buckets={completionTrend(activity)} />
        </Card>
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card
        title={`Open P0 blockers (${stats.openP0.length})`}
        subtitle="Everything that must close before the first BAA"
      >
        {stats.openP0.length === 0 ? (
          <p className="flex items-center gap-2 text-xs text-green-700 py-3">
            <ShieldCheck size={14} /> No P0 items remain open.
          </p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {stats.openP0.map((item) => (
              <ItemRow key={item.id} item={item} onOpen={onOpenItem} />
            ))}
          </ul>
        )}
      </Card>

      <div className="space-y-4">
        <Card title={`Blocked (${stats.blocked.length})`}>
          {stats.blocked.length === 0 ? (
            <p className="flex items-center gap-2 text-xs text-gray-400 py-3">
              <Timer size={14} /> Nothing is blocked.
            </p>
          ) : (
            <ul className="space-y-2">
              {stats.blocked.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => onOpenItem(item)}
                    className="w-full text-left hover:bg-gray-50 rounded px-1 -mx-1 py-1 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <AlertTriangle size={12} className="text-red-500 shrink-0" />
                      <span className="text-xs font-medium text-gray-800">{item.title}</span>
                    </span>
                    <span className="block text-[11px] text-gray-500 mt-0.5 pl-5">
                      {item.blockedReason || 'No reason recorded'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Recent activity">
          <ActivityFeed activity={activity} items={items} onOpen={onOpenItem} />
        </Card>
      </div>
    </div>

    <Card title="Overall status" subtitle={`${stats.overall.total} tracked items of real work`}>
      <div className="flex flex-wrap items-center gap-4 mb-3">
        {(['done', 'partial', 'in_progress', 'not_started'] as const).map((status) => (
          <div key={status} className="flex items-center gap-2">
            <StatusPill status={status} />
            <span className="text-sm font-semibold text-gray-900">{stats.overall[status]}</span>
          </div>
        ))}
        <span className="flex items-center gap-1.5 text-xs text-gray-400">
          <HelpCircle size={12} />
          Trust-list rows are excluded — they mirror the same work.
        </span>
      </div>
      <StackedProgressBar breakdown={stats.overall} height="h-3" />
    </Card>
  </div>
);
